import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { formatFileSize } from '@/utils/fileManagerUtils';
import DestinationFolderSelector from './DestinationFolderSelector';

export interface UploadConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** File to be uploaded */
  file: File | null;

  /** Current directory path (default destination) */
  currentPath: string;

  /** Array of existing folder paths for autocomplete */
  existingFolders: string[];

  /** Callback when upload is confirmed */
  onConfirm: (file: File, destinationPath: string) => void;

  /** Callback when upload is cancelled */
  onCancel: () => void;
}

/**
 * Validate folder path format.
 *
 * Validation Rules:
 * - No path traversal sequences (..)
 * - No double slashes (//)
 * - No control characters
 *
 * Leading/trailing slashes are silently stripped rather than rejected,
 * since users naturally type "/" when thinking about folder paths.
 *
 * @param path - Folder path to validate
 * @returns Error message if invalid, null if valid
 */
function validateFolderPath(path: string): string | null {
  // Empty path (root) is valid
  if (path === '') {
    return null;
  }

  // Check for path traversal
  if (path.includes('..')) {
    return 'Path cannot contain ".." (path traversal)';
  }

  // Check for double slashes
  if (path.includes('//')) {
    return 'Path cannot contain double slashes (//)';
  }

  // Check for control characters
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f]/.test(path)) {
    return 'Path cannot contain control characters';
  }

  return null;
}

/**
 * Normalize a destination path by stripping leading/trailing slashes.
 * This allows users to type "/reports/" and have it treated as "reports/".
 */
function normalizeDestinationPath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '');
}

/**
 * UploadConfirmDialog Component
 *
 * Displays a confirmation dialog for file uploads with destination folder selection.
 *
 * Features:
 * - Shows filename and file size
 * - Destination folder selector with autocomplete
 * - Defaults to current directory
 * - Allows typing new folder paths
 * - Validates folder path format
 * - Confirms upload with selected destination
 *
 * **Validates: Requirements 3.3.3, 3.3.4, 3.3.5, 3.4.3**
 */
const UploadConfirmDialog: React.FC<UploadConfirmDialogProps> = ({
  open,
  file,
  currentPath,
  existingFolders,
  onConfirm,
  onCancel,
}) => {
  // State for destination path
  const [destinationPath, setDestinationPath] = useState(currentPath);
  const [pathError, setPathError] = useState<string | null>(null);

  // Reset destination path when dialog opens or currentPath changes
  useEffect(() => {
    if (open) {
      setDestinationPath(currentPath);
      setPathError(null);
    }
  }, [open, currentPath]);

  // Handle destination path change with validation
  const handlePathChange = (path: string) => {
    setDestinationPath(path);
    const error = validateFolderPath(path);
    setPathError(error);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!file) return;

    // Validate path before confirming
    const error = validateFolderPath(destinationPath);
    if (error) {
      setPathError(error);
      return;
    }

    // Normalize: strip leading/trailing slashes so "/reports/" becomes "reports/"
    const normalized = normalizeDestinationPath(destinationPath);
    onConfirm(file, normalized);
  };

  // Handle cancel
  const handleCancel = () => {
    setDestinationPath(currentPath);
    setPathError(null);
    onCancel();
  };

  if (!file) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="upload-confirm-dialog-title"
    >
      <DialogTitle
        id="upload-confirm-dialog-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <CloudUploadIcon color="primary" />
        Confirm Upload
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            File Details
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <FileIcon color="action" fontSize="large" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" fontWeight={500}>
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(file.size)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Destination
          </Typography>

          <DestinationFolderSelector
            currentPath={currentPath}
            existingFolders={existingFolders}
            value={destinationPath}
            onChange={handlePathChange}
            error={pathError || undefined}
          />
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          Uploaded files will appear as &ldquo;Processing&rdquo; until backend
          processing is complete.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!!pathError}
          startIcon={<CloudUploadIcon />}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadConfirmDialog;
